/**
 * Login page: email + phone OTP sign-in, with links out to the public shuimir.com site.
 */
import React, { useState, useEffect } from 'react';
import logo from '../assets/Grey Imports Logo.png'; // Make sure to add the logo to your assets
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import {
  MdCalendarMonth,
  MdEmail,
  MdLanguage,
  MdLocalOffer,
  MdLockOutline,
  MdOpenInNew,
  MdOutlineArticle,
  MdPhoneIphone,
  MdShield,
  MdTrendingUp,
  MdVerifiedUser,
} from 'react-icons/md';

const SHUIMIR_BASE_URL = 'https://www.shuimir.com/';
const SHUIMIR_SECTION_NAV_DELAY_MS = 2000;

// Opens shuimir.com first, then moves to the section URL after a short delay.
// Modifier-key clicks (open in new tab, etc.) are left alone.
const openShuimirSection = (event, sectionUrl) => {
  if (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  ) {
    return;
  }

  event.preventDefault();

  const newWindow = window.open(SHUIMIR_BASE_URL, '_blank');
  if (!newWindow) {
    window.open(sectionUrl, '_blank');
    return;
  }

  window.setTimeout(() => {
    if (newWindow.closed) return;
    newWindow.location.href = sectionUrl;
  }, SHUIMIR_SECTION_NAV_DELAY_MS);
};

const PHONE_ALLOWED_CHARACTERS_REGEX = /^[0-9+\-()\s]*$/;

const Login = () => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [cooldownTime, setCooldownTime] = useState(0);
  const { user, userRole, loading: authLoading, authError, sendOtp, verifyOtp } = useAuth();
  
  useEffect(() => {
    let timer;
    if (cooldownTime > 0) {
      timer = setInterval(() => {
        setCooldownTime((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldownTime]);

  // Sends OTP to the provided email and phone number; shows the OTP input on success.
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await sendOtp(email, phone);
      if (result.success) {
        setShowOtpInput(true);
        setRetryCount(0);
        setCooldownTime(60);
      }
    } finally {
      setLoading(false);
    }
  };

  // Resends OTP; blocked during cooldown and after 2 retries.
  const handleResendOtp = async () => {
    if (retryCount >= 2 || cooldownTime > 0) return;
    
    setLoading(true);
    try {
      const result = await sendOtp(email, phone);
      if (result.success) {
        setRetryCount((prev) => prev + 1);
        setCooldownTime(60);
        setOtp('');
      }
    } finally {
      setLoading(false);
    }
  };

  // Verifies the entered OTP; on success, the user is logged in and redirected.
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await verifyOtp(otp);
    } finally {
      setLoading(false);
    }
  };

  const featureCards = [
    {
      title: 'Latest Reports',
      description: 'Recent model reports',
      icon: MdOutlineArticle,
      href: 'https://www.shuimir.com/#NewReport',
    },
    {
      title: 'VIN Check',
      description: 'Verify Unique ID with VIN',
      icon: MdShield,
      href: 'https://www.shuimir.com/#VerifyVIN',
    },
    {
      title: 'SEVS Expiry',
      description: 'Expiry information',
      icon: MdCalendarMonth,
      href: 'https://www.shuimir.com/#SEVExpiry',
    },
    {
      title: 'All Model Reports',
      description: 'All reports we offer',
      icon: MdLocalOffer,
      href: 'https://www.shuimir.com/#MREdata',
    },
    {
      title: 'Top Selling Model Reports',
      description: 'Our most popular reports',
      icon: MdTrendingUp,
      href: 'https://www.shuimir.com/#TopModel',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="flex-grow flex items-center justify-center px-4 py-5 sm:px-6 lg:px-8 2xl:px-6">
        <div className="grid w-full max-w-[min(90vw,150rem)] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl md:grid-cols-[1.06fr_0.94fr]">
          <section className="relative min-h-[31rem] overflow-hidden bg-white px-6 py-7 text-gray-950 sm:px-8 md:min-h-[32rem]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_84%_38%,rgba(248,113,113,0.22),transparent_28%),linear-gradient(135deg,#ffffff_0%,#f8fafc_52%,#f3f4f6_100%)]" />
            <div className="absolute right-[-7rem] top-14 h-80 w-[36rem] rounded-full border-[8px] border-red-200/70 opacity-80" />
            <div className="absolute bottom-16 right-[-8rem] h-24 w-[39rem] -rotate-6 rounded-full bg-red-100/80 blur-2xl" />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.96)_0%,rgba(255,255,255,0.9)_48%,rgba(255,255,255,0.58)_100%)]" />

            <div className="relative flex h-full flex-col justify-between gap-6 2xl:mx-auto 2xl:max-w-3xl 2xl:items-center 2xl:text-center">
              <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_13rem] xl:items-center 2xl:grid-cols-1">
                <div className="space-y-5 text-center xl:text-left 2xl:text-center">
                  <div className="flex items-center justify-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-red-600 xl:justify-start 2xl:justify-center">
                    <span className="h-px w-11 bg-red-600" />
                    Shuimir.com
                  </div>

                  <div className="mx-auto max-w-xl xl:mx-0 xl:max-w-md 2xl:mx-auto">
                    <h1 className="text-3xl font-bold tracking-tight text-gray-950 sm:text-4xl xl:text-5xl">
                      Import Intelligence Made <span className="text-red-600">Simple</span>
                    </h1>
                    <p className="mt-3 max-w-md text-sm leading-6 text-gray-600 sm:text-base sm:leading-7">
                      Access VIN verification, SEVS expiry tracking, and model reports from one trusted public platform.
                    </p>
                  </div>

                  <div className="flex flex-wrap justify-center gap-3 xl:justify-start 2xl:justify-center">
                    <a
                      href="https://www.shuimir.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-red-100 transition duration-200 hover:-translate-y-0.5 hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
                    >
                      <MdLanguage className="h-4 w-4" />
                      Explore Shuimir.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="space-y-4 2xl:mx-auto 2xl:max-w-5xl">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  {featureCards.map(({ title, description, icon: Icon, href }) => (
                    <a
                      key={title}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => openShuimirSection(event, href)}
                      className="flex h-full min-h-[7.5rem] flex-col rounded-md border border-gray-100 bg-white p-3 shadow-md shadow-gray-200/70 transition duration-200 hover:-translate-y-0.5 hover:border-red-200 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 xl:min-h-[9rem]"
                    >
                      <Icon className="h-5 w-5 shrink-0 text-red-500" />
                      <p className="mt-3 min-h-[2rem] text-xs font-semibold leading-4 text-gray-950">{title}</p>
                      <p className="mt-1.5 hidden min-h-[2rem] text-[11px] leading-4 text-gray-500 xl:block">{description}</p>
                      <span className="mt-auto block h-0.5 w-7 shrink-0 self-start bg-red-600" aria-hidden="true" />
                    </a>
                  ))}
                </div>

                <div className="flex items-start gap-3 text-sm text-gray-600 2xl:justify-center">
                  <MdVerifiedUser className="mt-0.5 h-6 w-6 flex-none text-red-500" />
                  <div>
                    <p className="font-semibold text-gray-950">Trusted by our customers across Australia</p>
                    <p className="mt-1 text-xs text-gray-500">Reliable data. Accurate reports. Better decisions.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="flex items-center justify-center bg-white px-6 py-7 sm:px-12 md:min-h-[32rem]">
            <div className="w-full max-w-[24rem] space-y-5">
              <div>
                <img
                  className="mx-auto h-[4.25rem] w-auto"
                  src={logo}
                  alt="Grey Imports"
                />
                <h2 className="mt-5 text-center text-2xl font-bold tracking-tight text-gray-900">
                  Welcome!
                </h2>
                <p className="mt-2 text-center text-xs text-gray-500">
                  Register or sign in to your Model Report account
                </p>
              </div>

              {authError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {authError}
                </div>
              )}

              <form className="space-y-4" onSubmit={showOtpInput ? handleVerifyOtp : handleSendOtp}>
                <div>
                  <label htmlFor="email" className="block text-xs font-semibold leading-6 text-gray-900">
                    Email Address
                  </label>
                  <div className="relative mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      disabled={showOtpInput}
                      className={`block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ${
                        showOtpInput 
                          ? 'bg-gray-100 ring-gray-200' 
                          : 'ring-gray-300'
                      } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-red-600 sm:text-sm sm:leading-6`}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                    />
                    <MdEmail className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-semibold leading-6 text-gray-900">
                    Phone Number
                  </label>
                  <div className="relative mt-2">
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      pattern="[0-9+\-()\s]+"
                      autoComplete="tel"
                      required
                      disabled={showOtpInput}
                      className={`block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ${
                        showOtpInput 
                          ? 'bg-gray-100 ring-gray-200' 
                          : 'ring-gray-300'
                      } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-red-600 sm:text-sm sm:leading-6`}
                      value={phone}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        if (PHONE_ALLOWED_CHARACTERS_REGEX.test(nextValue)) {
                          setPhone(nextValue);
                        }
                      }}
                      placeholder="e.g. 0412345678"
                    />
                    <MdPhoneIphone className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                  {!showOtpInput && (
                    <p className="mt-2 text-sm text-gray-500">
                      Enter your Australian mobile number (e.g. 0412345678)
                    </p>
                  )}
                </div>

                {showOtpInput && (
                  <div>
                    <label htmlFor="otp" className="block text-xs font-semibold leading-6 text-gray-900">
                      Enter verification code
                    </label>
                    <div className="relative mt-2">
                      <input
                        id="otp"
                        type="text"
                        required
                        maxLength="6"
                        pattern="\d{6}"
                        className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-red-600 sm:text-sm sm:leading-6"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder="Enter 6-digit code"
                      />
                      <MdLockOutline className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                    <div className="mt-2 flex items-start flex-col justify-between">
                      <p className="text-sm text-gray-500">
                        Please check your phone for the verification code
                      </p>
                      {retryCount < 2 && (
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={cooldownTime > 0 || loading}
                          className="text-sm font-semibold text-red-600 hover:text-red-500 disabled:text-gray-400"
                        >
                          {cooldownTime > 0 
                            ? `Resend in ${cooldownTime}s` 
                            : 'Resend code'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className={`flex ${showOtpInput ? 'gap-3' : ''}`}>
                  {showOtpInput && (
                    <button
                      type="button"
                      onClick={() => setShowOtpInput(false)}
                      className="flex-1 rounded-md bg-white px-3 py-2 text-sm font-semibold leading-6 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Back
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold leading-6 text-white shadow-lg shadow-red-100 transition duration-200 hover:-translate-y-0.5 hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:translate-y-0 disabled:opacity-50"
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      <MdLockOutline className="h-4 w-4" />
                    {loading 
                      ? (showOtpInput ? 'Verifying...' : 'Sending...') 
                      : (showOtpInput ? 'Verify' : 'Send OTP')
                    }
                    </span>
                  </button>
                </div>
              </form>

              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span className="h-px flex-1 bg-gray-200" />
                or
                <span className="h-px flex-1 bg-gray-200" />
              </div>

              <p className="text-center text-sm text-gray-500">
                Need public information?{' '}
                <a
                  href="https://www.shuimir.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-semibold text-red-600 hover:text-red-500"
                >
                  Visit shuimir.com
                  <MdOpenInNew className="h-3.5 w-3.5" />
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
      <Footer portal="public" />
    </div>
  );
};

export default Login; 